import { RadioGroup } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../../../../lib/cn';

export interface ChoiceOption<V extends string> {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

/**
 * Wybór jednej opcji jako karty z kropką (Figma „Zaproś osobę”: Rola). Radix RadioGroup: strzałki, Spacja,
 * czytniki ekranu. `columns` = karty obok siebie (desktop), inaczej lista pionowa.
 * Komponent lokalny modułu: w `ui/` nie ma jeszcze RadioCards (propozycja do przeniesienia).
 */
export function ChoiceCards<V extends string>({
  value,
  onValueChange,
  options,
  columns = false,
  disabled = false,
  'aria-label': ariaLabel,
  className,
}: {
  value: V;
  onValueChange: (value: V) => void;
  options: ChoiceOption<V>[];
  columns?: boolean;
  disabled?: boolean;
  'aria-label': string;
  className?: string;
}) {
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={(v) => onValueChange(v as V)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn('grid gap-2', columns ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1', className)}
    >
      {options.map((option) => (
        <RadioGroup.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            'group flex items-start gap-2.5 rounded-lg border border-line bg-canvas px-3.5 py-3 text-left transition-colors',
            'hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            'data-[state=checked]:border-accent data-[state=checked]:bg-accent-subtle',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <span
            aria-hidden
            className={cn(
              'mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-line-strong bg-canvas',
              'group-data-[state=checked]:border-accent',
            )}
          >
            <RadioGroup.Indicator className="block size-2 rounded-full bg-accent" />
          </span>
          <span className="min-w-0">
            <span className="block text-body-s font-semibold text-fg">{option.label}</span>
            {option.description && (
              <span className="mt-0.5 block text-caption text-fg-secondary">{option.description}</span>
            )}
          </span>
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
